import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, LogIn, User } from 'lucide-react';

interface LoginPageProps {
  onPasswordLogin: (email: string, password: string) => Promise<void>;
  onLineLogin: () => void;
  error?: string | null;
  isLoading?: boolean;
}

export function LoginPage({ onPasswordLogin, onLineLogin, error, isLoading }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    if (!email || !password) {
      setLoginError('กรุณากรอกอีเมลและรหัสผ่าน');
      return;
    }

    try {
      await onPasswordLogin(email, password);
    } catch (err: any) {
      setLoginError(err.message || 'เข้าสู่ระบบไม่สำเร็จ');
    }
  };

  const displayError = loginError || error;

  return (
    <div className="min-h-screen bg-mesh-ocean flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass rounded-2xl p-8 max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🌊</div>
          <h2 className="text-3xl font-bold text-white mb-2">TrustBridge</h2>
          <p className="text-white/60">
            ฉันจะเป็นเพื่อนคู่ใจของคุณ<br />
            ช่วยให้คุณเชื่อใจได้อย่างปลอดภัย
          </p>
        </div>

        {/* Error Display */}
        {displayError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-400/30"
          >
            <p className="text-red-300 text-sm">{displayError}</p>
          </motion.div>
        )}

        {/* Password Login Form */}
        <form onSubmit={handlePasswordLogin} className="space-y-4 mb-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
              อีเมล
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="buyer@test.com"
                disabled={isLoading}
                className="w-full pl-11 pr-4 py-3 rounded-xl glass text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-teal-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
              รหัสผ่าน
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                disabled={isLoading}
                className="w-full pl-11 pr-4 py-3 rounded-xl glass text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-teal-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={isLoading}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(20,184,166,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>กำลังเข้าสู่ระบบ...</span>
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                <span>เข้าสู่ระบบ</span>
              </>
            )}
          </motion.button>
        </form>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/20"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 glass rounded-lg text-white/60">หรือ</span>
          </div>
        </div>

        {/* LINE Login Button */}
        <motion.button
          onClick={onLineLogin}
          disabled={isLoading}
          whileTap={{ scale: isLoading ? 1 : 0.95 }}
          className="w-full py-3 rounded-xl bg-[#00B900] text-white font-semibold hover:bg-[#00A000] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <User className="w-5 h-5" />
          <span>เข้าสู่ระบบด้วย LINE</span>
        </motion.button>

        {/* Helper Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-white/40">
            ทดสอบ: buyer@test.com / test123
          </p>
        </div>
      </motion.div>
    </div>
  );
}
